import { AccumulatorClasses, ILambdaLogger, Milliseconds } from '@iotv/iotv-v3-types'

import { getNiceDate, getNiceDates, TimeBucketUnits, AutoBucketHistory } from '../..'
import { BucketStrategy, ValidBusinessObject } from '../../types'
import { HistoryStore } from '../..'
import { getApportionToTimeBucketsMap } from '../..'
import { hourlyYearBasedAccessorFromTimestamp } from '../..'
import { AccumulationRequest } from '../..'

const prettyAbh = (abh: AutoBucketHistory) => {
  let abhStr = ``

  const states = abh.getStateSnapshotArray()

  for (const state of states) {
    abhStr += `${getNiceDate(state.timestamp)}: ${JSON.stringify(state.state)}\n`
  }

  return abhStr
}

/** Helper fn for generating accumulation requests from inside CalculateState
 * Finds completed hours by using an autobuckethistory
 *
   @param currentTimestamp - Timestamp of the current message 
   @param entity - The entity connected to the accumulator 
   @param currentMessage - The values in the current message being accumulated in CalculateState 
   @param recentStateHistory - Record of all readings of this entity 
   @param logger - Logger for errors etc 
   @param accumulatorClass - The type of accumulator to generate requests for (right now only HourlyYearBased supported)
   @param subHourTzOffset - For hourly year based normal accumulators, the logic is tweaked if the tz offset from UTC is not an even number of hours. 
   @param bucketStrategyMap - Map of reading keys to bucket strategies. Defaults to Apportion if not present 
 * */
export const generateAccumulationRequests = (
  currentTimestamp: number,
  entity: ValidBusinessObject,
  currentMessage: { [k: string]: number | null | undefined },
  recentStateHistory: HistoryStore,
  logger: ILambdaLogger,
  accumulatorClass: AccumulatorClasses,
  subHourTzOffset: number = 0,
  bucketStrategyMap?: { [k: string]: BucketStrategy },
) => {
  logger.debug('Generating Normal Accumulations for', getNiceDate(currentTimestamp), currentMessage)

  if (accumulatorClass !== AccumulatorClasses.HourlyYearBasedNormalAccumulator) {
    logger.error(`Attempting to generate accumulation requests with unknown accumulator class: ${accumulatorClass}`)
    return
  }

  const timeBucketDef = { width: 1, unit: TimeBucketUnits.HOUR, offset: subHourTzOffset }

  /** Bucket this message */
  const curAbh = new AutoBucketHistory({
    timeBucketDef,
    defaultBucketStrategy: BucketStrategy.APPORTION,
  })

  if (bucketStrategyMap) {
    curAbh.loadKeyStrategyMap(bucketStrategyMap)
  }

  const nonNullTimestampsBeforeCur = recentStateHistory.getIndexLastNonNullKeyLessThanTimestamp(currentTimestamp, 'timestamps')
  curAbh.pushKeywise({ timestamp: currentTimestamp, state: currentMessage }, nonNullTimestampsBeforeCur)

  logger.debug(`Cur Message:\n${prettyAbh(curAbh)}`)

  /** Get time bucket containing latest timestamp */
  // If the timestamp is equal to the very end of the bucket, then we know it's done. See comment 2 lines below
  const timeBucketOfCurrentTimestamp = getApportionToTimeBucketsMap({
    timeBucketDef,
    measureRange: { fromTimestamp: currentTimestamp, toTimestamp: currentTimestamp + 1 }, // +1 gives us the smallest tiny overlap. Ensures that a timestamp like 12:00 is in the 12:00-13:00 bucket. This works here because if the time is exactly 12:00, we know bucket 11:00-12:00 is complete, because no possible additions can be made to 11:00.
  })

  const curBucketTs = timeBucketOfCurrentTimestamp[0].bucket[0]
  logger.debug('Bucket of Current TS', getNiceDate(curBucketTs))

  logger.debug('First Ts in Bucketed', getNiceDate(curAbh.timestamp[0]))

  /** Right now we've only bucketed the most recent message, but we want to get the full value of the earliest bucket, so we actually need to bucket the RSH (plus the current messages, because the RSH doesn't have them yet). */
  const relevantReadings = [
    ...recentStateHistory.sliceByTimestamp([curAbh.timestamp[0], currentTimestamp]).getStateSnapshotArray(), // I think slicing by timestamp might be off by 1
    //...curAbh.getStateSnapshotArray(),
    { timestamp: currentTimestamp, state: currentMessage },
  ] //.filter((snap) => snap.timestamp < curBucketTs); // We can use the curAbh timestamp here because it will be the start of the first bucket, which we are reaching into the RSH to find.

  logger.debug('Relevant Readings', relevantReadings, getNiceDates(relevantReadings.map((r) => r.timestamp)))

  if (relevantReadings.length === 0) return []

  const relevantBuckets = new AutoBucketHistory({ timeBucketDef, defaultBucketStrategy: BucketStrategy.APPORTION })

  // Find the latest non null timestamps before the relevant readings, so that bucketing the RSH is accurate
  const nonNullTimestampsBeforeRelevant = recentStateHistory.getIndexLastNonNullKeyLessThanTimestamp(curAbh.timestamp[0], 'timestamps')
  // Bucket the RSH with all readings from candidate buckets
  relevantBuckets.pushKeywise(relevantReadings[0], nonNullTimestampsBeforeRelevant)
  for (let idx = 1; idx < relevantReadings.length; idx++) {
    relevantBuckets.pushKeywise(relevantReadings[idx])
  }

  // Slice by timestamp gets all timestamps between. But timestamps of bucketed readings represent the latest point of that reading. That means it's possible a relevant reading gets apportioned into a bucket before the latest incomplete bucket.
  // For this reason, we filter not only by buckets whose timestamps are before the bucket of the current timestamp, but also timestamps that are later (or equal) than the earliest bucket found when we bucket the current message (the latest known to not be accumulated yet).

  // Fun Fact: We actually also filter on this when we only accumulate readings whose latest timestamp needs to be within or before the bucket. So as long as we don't include accumulation requests who have an empty update, the second part of the filter here is actually unneeded.
  // I have both though, because the time spent understanding this problem demands some material difference in the file.

  // Now we have the full range of values
  const completeBuckets = relevantBuckets.getStateSnapshotArray().filter((s) => s.timestamp < curBucketTs && s.timestamp >= curAbh.timestamp[0])
  logger.debug('Complete Buckets', completeBuckets, getNiceDates(completeBuckets.map((s) => s.timestamp)))

  // Turn complete buckets into Accumulation Requests (normals)
  // Some keys may be in the earliest bucket but their latest reading is after. So we need to make sure we only accumulate keys who's latest timestamp before the **current** message is in or before the bucket
  let accumulationRequests: AccumulationRequest[] = []

  completeBuckets.forEach((completeBucket) => {
    let request: AccumulationRequest = {
      accessor: hourlyYearBasedAccessorFromTimestamp(completeBucket.timestamp),
      accumulationClassName: AccumulatorClasses.HourlyYearBasedNormalAccumulator,
      update: {},
      validBusinessObject: entity,
    }

    Object.entries(completeBucket.state).forEach(([readingKey, value]) => {
      if (value !== null && (nonNullTimestampsBeforeCur[readingKey] ?? -Infinity) < completeBucket.timestamp + Milliseconds.HOUR) {
        request.update[readingKey] = value
      }
    })

    if (Object.keys(request.update).length > 0) accumulationRequests.push(request)
  })

  return accumulationRequests
}
